import {
  enumBoxHeightMode,
  enumBoxWidthMode,
  enumChartRenderOrder,
  enumElementLayer,
  kBoxResizerSize,
  kHalfBoxResizerSize
} from "../constants";
import { GetBaseBoxSize } from "../utils/GetBaseBoxSize";
import { QElement } from "./QElement";
import { ThicknessAdjuster } from "../utils/ThicknessAdjuster";
import { Globals } from "./Globals";

export class QBoxResizer extends QElement {
  constructor() {
    super();
    this.fChartBox = null;
    this.fDeltaDefX = 0;
    this.fDeltaDefY = 0;
    this.fAllowDeltaX = false;
    this.fAllowDeltaY = false;
    this.fKeepAspectRatio = false;
  }

  InitBoxResizer(chartBox, allowDeltaX, allowDeltaY, aspectRatio) {
    this.fAllowDeltaX = allowDeltaX;
    this.fAllowDeltaY = allowDeltaY;
    this.fKeepAspectRatio = aspectRatio;

    this.fChartBox = chartBox;
    this.SizeElement(kBoxResizerSize, kBoxResizerSize);
    this.SetDraggable(true);
    this.SetZPlane(enumChartRenderOrder.overlay);
    this.SetLayer(chartBox.GetLayer());

    this.SetDragStartCmdCode("BoxResizeStarted");
    this.SetControlReleaseCmdCode("SelectedBoxResized");
  }

  SetAllowDeltaY(allowDeltaY) {
    this.fAllowDeltaY = allowDeltaY;
  }

  SetAllowDeltaX(allowDeltaX) {
    this.fAllowDeltaX = allowDeltaX;
  }

  SetKeepAspectRatio(keepAspectRatio) {
    this.fKeepAspectRatio = keepAspectRatio;
  }

  GetAssociatedBox() {
    return this.fChartBox;
  }

  GetPositionX() {
    var w = this.fChartBox.GetWidth();

    var xPos = this.fChartBox.GetPositionX() + w - kHalfBoxResizerSize;
    return xPos;
  }

  GetPositionY() {
    var h = this.fChartBox.GetHeight();

    var yPos = this.fChartBox.GetPositionY() + h - kHalfBoxResizerSize;
    return yPos;
  }

  ResizeComplete() {
    var layout = this.fChartBox?.GetLayout();
    if (
      Globals._uiConfig.InBackgroundEditorMode() &&
      this.fChartBox.GetLayer() === enumElementLayer.master
    ) {
      Globals._wsMgr?.fCanvasPanel?.UpdateLayoutBackgroundMgr(layout);
    }
    this.fChartBox["activebox"] = false;
    this.fDeltaDefX = 0;
    this.fDeltaDefY = 0;
  }

  MoveElement(deltaX, deltaY) {
    if (!this.fAllowDeltaX) deltaX = 0;

    if (!this.fAllowDeltaY) deltaY = 0;

    if (deltaX == 0) {
      if (deltaY == 0) return;
    }

    if (this.fDeltaDefX > 0 && deltaX > 0) {
      this.fDeltaDefX = this.fDeltaDefX - deltaX;
      if (this.fDeltaDefX < 0) {
        deltaX = -this.fDeltaDefX;
        this.fDeltaDefX = 0;
      } else deltaX = 0;
    }

    if (this.fDeltaDefY > 0 && deltaY > 0) {
      this.fDeltaDefY = this.fDeltaDefY - deltaY;
      if (this.fDeltaDefY < 0) {
        deltaY = -this.fDeltaDefY;
        this.fDeltaDefY = 0;
      } else deltaY = 0;
    }

    var l = this.fChartBox.GetLayout();
    if (this.fKeepAspectRatio) {
      var pt = GetBaseBoxSize(l);
      var baseWidth = pt.fX;
      var baseHeight = pt.fY;

      var curWidth = l.GetBoxWidth();
      var curHeight = l.GetBoxHeight(false);

      var newWidth = curWidth + deltaX;
      var newHeight = curHeight + deltaY;

      if (baseWidth > baseHeight) newHeight = Math.round((newWidth * baseHeight) / baseWidth);
      else newWidth = Math.round((newHeight * baseWidth) / baseHeight);

      deltaX = newWidth - curWidth;
      deltaY = newHeight - curHeight;
    }

    this.fChartBox["activebox"] = true;

    if (deltaX != 0) {
      var sizingMode = l.GetWidthSizingMode();
      if (sizingMode != enumBoxWidthMode.fixedwidth)
        l.SetWidthSizingMode(enumBoxWidthMode.fixedwidth);
      var actualDelta = l.ChangeBoxWidth(deltaX);
      if (actualDelta != deltaX) this.fDeltaDefX += Math.abs(deltaX - actualDelta);
    }

    if (deltaY != 0) {
      // DeltaY is only set when resizing pictures - so this is not very robust.
      var sizingModeV = l.GetHeightSizingMode();
      if (sizingModeV != enumBoxHeightMode.fixedheight)
        l.SetHeightSizingMode(enumBoxWidthMode.fixedheight);
      var actualDelta = l.ChangeBoxHeight(deltaY);

      var c1 = l.GetCellDesc(1, 1);
      var curHeight = c1.GetCellHeight();
      c1.SetCellHeight(curHeight + actualDelta);

      if (actualDelta != deltaY) this.fDeltaDefY += Math.abs(deltaY - actualDelta);
    }

    l.CalculateLayout();

    this.fChartBox.ResetBoxLayout(true);
  }

  DoRenderElement(drawBuffer) {
    var ctx = drawBuffer.getContext("2d");

    var thickness = 1;

    ctx.save();
    ctx.strokeStyle = "#000000";
    ctx.fillStyle = "#CAE5FF";
    ctx.lineWidth = thickness;
    ctx.lineJoin = "miter";
    ctx.lineCap = "square";

    // Draw slider based on fCurrentVal
    var sx = this.GetPositionX() + ThicknessAdjuster(thickness);
    var sy = this.GetPositionY() + ThicknessAdjuster(thickness);

    var ex = sx + kBoxResizerSize;
    var ey = sy + kBoxResizerSize;

    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineTo(ex, sy);
    ctx.lineTo(ex, ey);
    ctx.lineTo(sx, ey);
    ctx.lineTo(sx, sy);
    ctx.closePath();

    ctx.stroke();
    ctx.fill();

    ctx.restore();
  }

  IsShown() {
    return this.fChartBox.IsResizerShown();
  }

  GetCursor() {
    if (this.fAllowDeltaY && this.fAllowDeltaX) return "se-resize";
    else {
      if (this.fAllowDeltaX) return "ew-resize";
      if (this.fAllowDeltaY) return "ns-resize";

      return "not-allowed";
    }
  }

  GetHitBox() {
    var rightLeftThreshold = 10;
    var bottomTopThreshold = 10;
    if (Globals._mobileDevice) {
      var sf = Globals._wsMgr.fCanvasPanel.GetScaleFactor();
      rightLeftThreshold = Math.floor(rightLeftThreshold / sf);
      bottomTopThreshold = Math.floor(bottomTopThreshold / sf);
    }
    return this.CreateBoundingHitBox(
      rightLeftThreshold,
      rightLeftThreshold,
      bottomTopThreshold,
      bottomTopThreshold
    );
  }

  // TORK - Not rendered in PDF
  DoAltRender(altCanvas) {
    // Doesn't pass through to Publish
  }
}
